import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { WaterlilyClient } from '@/api/waterlily';
import { selectFeatureFlags } from '@/model/selectors';

/**
 * ManhattanLife OmniFlex STC is billed monthly only, so the product is hidden
 * for any other payment option — the same gate traditional LTC applies via its
 * ELIGIBLE_PAYMENT_OPTIONS list.
 */
export const SHORT_TERM_CARE_ELIGIBLE_PAYMENT_OPTIONS = ['Monthly'];

/**
 * Daily benefit amounts offered in the shop. The carrier sells $50-$400 in $10
 * increments (36 values), which at 4 benefit periods x 4 elimination periods
 * would be 576 cards per rate class — roughly 4x what traditional LTC renders.
 * Showing $50 steps gives 8 x 4 x 4 = 128, in line with traditional LTC's 144.
 * The finer increments remain in the table and can be exposed later.
 *
 * $350 and $400 exceed the Home Health Care rider's $300 ceiling; those cards
 * carry the rider at its maximum, so the facility and home daily benefits
 * differ and both are shown on the card.
 */
export const SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS = [50, 100, 150, 200, 250, 300, 350, 400];

/**
 * The 5% Simple Inflation rider election. KY's 3% compound variant is a
 * separate option the carrier offers but we have not swept, so it is not
 * representable here yet.
 */
export type ShortTermCareInflationOption = 'none' | 'simple5';

export interface ShortTermCareQuotesFilters {
  dailyBenefitAmount?: number;
  dailyBenefitAmounts?: number[];
  benefitPeriodDays?: number;
  eliminationPeriodDays?: number;
  tobacco?: boolean;
  /** '' for no discount, 'household' for the spousal discount. */
  discountKey?: string;
  /**
   * 'none' or 'simple5' (the 5% Simple Inflation rider). Every election is
   * stored under both, so this is single-valued rather than a list — sending
   * nothing would return each configuration twice, about 40% apart.
   */
  inflationOption?: ShortTermCareInflationOption;
  /**
   * How the Home Health Care rider is included. 'matchFacility' — the shop
   * default — returns only quotes that carry the rider, elected to match each
   * card's own daily benefit and periods. 'none' returns base-policy quotes.
   */
  homeRider?: 'none' | 'matchFacility';
  /**
   * Lets the caller suppress the fetch for a filter state it already knows
   * returns nothing — an empty daily-benefit selection, say, which the
   * repository would otherwise read as "no filter" and answer in full.
   */
  enabled?: boolean;
}

interface UseShortTermCareQuotesProps extends ShortTermCareQuotesFilters {
  advisorId?: string;
  state?: string;
  issueAge?: number;
  paymentOption?: string;
}

export const shortTermCareQuoteKeys = {
  all: ['short-term-care-quotes'] as const,
  list: (params: Record<string, unknown>) =>
    ['short-term-care-quotes', 'list', params] as const,
};

/**
 * Stored short-term care rates for one applicant profile.
 *
 * Gated on quoteApplyManhattanLife: without the flag the query never fires, so
 * advisors who cannot see the product never request it. The server enforces
 * the same gate — this only avoids a guaranteed 403.
 *
 * Always send narrowing filters. An unfiltered profile is ~1,700 rows
 * (~1.4 MB); the endpoint supports filtering precisely so the browser does not
 * have to hold that.
 */
export function useShortTermCareQuotes({
  advisorId,
  state,
  issueAge,
  paymentOption,
  dailyBenefitAmount,
  dailyBenefitAmounts = SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS,
  benefitPeriodDays,
  eliminationPeriodDays,
  tobacco,
  discountKey,
  inflationOption = 'none',
  // The shop sells this product with home care included: the rider is what
  // makes it a plan an advisor would place, and a facility-only card reads as
  // a cheaper quote for the same coverage. Pass 'none' for base-only rates.
  homeRider = 'matchFacility',
  enabled = true,
}: UseShortTermCareQuotesProps) {
  const featureFlags = useSelector(selectFeatureFlags);
  // Fails closed: an unknown payment option hides the product rather than
  // showing monthly-only quotes alongside Single Pay or 10-Pay life quotes.
  const isPaymentOptionEligible =
    paymentOption !== undefined &&
    SHORT_TERM_CARE_ELIGIBLE_PAYMENT_OPTIONS.includes(paymentOption);
  const isEnabled =
    Boolean(featureFlags?.quoteApplyManhattanLife) && isPaymentOptionEligible && enabled;

  const payload = {
    advisorId,
    state,
    issueAge,
    dailyBenefitAmount,
    dailyBenefitAmounts,
    benefitPeriodDays,
    eliminationPeriodDays,
    tobacco,
    discountKey,
    inflationOption,
    homeRider,
  };

  const query = useQuery({
    queryKey: shortTermCareQuoteKeys.list(payload),
    queryFn: () =>
      WaterlilyClient.post<ShortTermCareQuote[]>('/short-term-care-quotes-query', payload),
    enabled: isEnabled && Boolean(advisorId) && Boolean(state) && issueAge !== undefined,
    // Filter changes mint a new query key. Without this the list unmounts and
    // flashes "Loading…" on every toggle, and the global gcTime of 10s means
    // even toggling back refetches.
    placeholderData: keepPreviousData,
  });

  return {
    // Never leak rows when the flag is off, regardless of anything cached.
    shortTermCareQuotes: isEnabled ? (query.data ?? []) : [],
    isLoading: isEnabled ? query.isLoading : false,
    error: query.error,
    isShortTermCareEnabled: isEnabled,
  };
}
